"use client";

import { useEffect } from "react";
import type { ThirdPartyCode } from "@/lib/thirdPartyCodes";

type Slot = "head" | "body-start" | "body-end";

// Cloning a parsed <script> into a freshly created element is required:
// scripts inserted into the DOM by setting innerHTML (or via <template>)
// are inert and never execute. A document.createElement('script') copy
// with the same attributes and content runs as soon as it's inserted.
function cloneScript(src: HTMLScriptElement): HTMLScriptElement {
  const out = document.createElement("script");
  for (const attr of Array.from(src.attributes)) {
    out.setAttribute(attr.name, attr.value);
  }
  if (src.textContent) out.textContent = src.textContent;
  return out;
}

function injectInto(codes: ThirdPartyCode[], slot: Slot): () => void {
  if (codes.length === 0) return () => {};

  let container: Node;
  let anchor: Node | null;
  if (slot === "head") {
    container = document.head;
    anchor = null;
  } else if (slot === "body-start") {
    container = document.body;
    anchor = document.body.firstChild;
  } else {
    container = document.body;
    anchor = null;
  }

  const inserted: Node[] = [];
  for (const code of codes) {
    const tpl = document.createElement("template");
    tpl.innerHTML = code.code;
    for (const child of Array.from(tpl.content.childNodes)) {
      const node =
        child.nodeName === "SCRIPT"
          ? cloneScript(child as HTMLScriptElement)
          : child;
      container.insertBefore(node, anchor);
      inserted.push(node);
    }
  }

  return () => {
    for (const node of inserted) node.parentNode?.removeChild(node);
  };
}

interface Props {
  insideHead: ThirdPartyCode[];
  afterOpenBody: ThirdPartyCode[];
  afterCloseBody: ThirdPartyCode[];
}

export default function ThirdPartyCodes({
  insideHead,
  afterOpenBody,
  afterCloseBody,
}: Props) {
  useEffect(() => {
    const cleanups = [
      injectInto(insideHead, "head"),
      injectInto(afterOpenBody, "body-start"),
      injectInto(afterCloseBody, "body-end"),
    ];
    return () => {
      for (const c of cleanups) c();
    };
  }, [insideHead, afterOpenBody, afterCloseBody]);

  return null;
}
